//@ts-check
import * as THREE from "three";
import viewerMgrInstance from '../ViewerMgr.js';

/**
 * @module Selection  
 */


// selection event
/**
 * Select Event
 * @param {MouseEvent} event - Mpuse event
 * @param {*} callBack - call back intersected object name
 * @returns {Object} Return success or error messages 
 * { status:  mesage: }
 */
export function pickElement(event, callBack) {
  var scope = this;
  try {
    if (!scope.isDragged) {
      if ((event != undefined || event != null) && (callBack != undefined || callBack != null)) {
        scope.raycastSetup(event);
        if (!scope.multipleSelectionFlagCheck) {
          scope.clearPickSelection();
        }
        var intersected = scope.raycaster.intersectObject(scope.modelScene, true);
        if (intersected.length > 0) {
          scope.applySelectionMaterial(intersected[0].object.name);
          callBack(intersected[0].object.name);
        } else {
          scope.clearPickSelection();
          callBack("");
        }
        //viewerMgrInstance.render();
        var returnValue = {
          "status": "success",
          "message": "Element selected successfully."
        }
        return returnValue;
      }
      else {
        var returnValue = {
          "status": "failed",
          "message": "ERROR : Event or callback is either undefined or null."
        }
        return returnValue;
      }
    }
  }
  catch (err) {
    var returnValue = {
      "status": "failed",
      "message": "System Error : " + err.message
    }
    return returnValue;
  }
}


/**
 * Applies selection material to a single element
 * @param {string} element_id - Element Id
 * @returns {Object} Status object
 */
export function applySelectionMaterial(element_id) {
  var scope = this;
  try {
    let child = scope.elementsMap.get(element_id);
    
    // Store original material once if not already stored
    if (!child.userData.originalMaterial) {
      child.userData.originalMaterial = child.material;
    }

    // Remember if the element was ghosted before selection
    if (child.userData.ghosted) {
      child.userData._wasGhosted = true;
      // Remove the regular ghosted flag since we're now selected
      child.userData.ghosted = false;
    }

    // Save filtered state if needed
    if (child.userData.filtered) {
      child.userData._wasFiltered = true;
      child.userData._filteredOpacity = child.userData.opacity || 0.3;
    }

    // Store original material state if not already stored
    if (!child.userData.originalMaterial && child.material !== scope.selectionMaterial) {
      child.userData.originalMaterial = child.material;
    }

    // Apply selection material
    child.userData.selectMaterial = scope.selectionMaterial;
    child.material = scope.selectionMaterial;
    
    // Remove from highlight array if it's there
    const hlIndex = scope.highLightArray ? scope.highLightArray.indexOf(child) : -1;
    if (hlIndex >= 0) {
      scope.highLightArray.splice(hlIndex, 1);
    }
    
    if (scope.clipActive) {
      child.material.clippingPlanes = scope.clippingPlanes;
      child.material.clipShadows = scope.clipShadows;
      child.material.clipIntersection = scope.clipIntersection;
    }
    
    // Add to selection tracking array
    if (!scope.intersectedArray.includes(child)) {
      scope.intersectedArray.push(child);
    }
    
    return { status: 'success', message: 'Selection material applied' };

  } catch (err) {
    return { status: 'failed', message: err.message };
  }
}


/**
 * Resets selected elements in scope.intersectedArray
 * @param {*} [render= false] - Render or not ?
 * @returns {Object} - Return success or error messages 
 * { status:  mesage: }
 */
export function clearPickSelection(render = false) {
  var scope = this;
  try {
    if (scope.intersectedArray.length >= 1) {
      scope.intersectedArray.forEach((element) => {
        if (element) {
          // First clean up selection state
          delete element.userData.selectMaterial;
          
          // If it was ghosted before selection, restore ghost material
          if (element.userData._wasGhosted) {
            element.material = scope.ghostMaterial;
            element.userData.ghosted = true;
            // Clean up temporary ghost state
            delete element.userData._wasGhosted;
          }
          // Otherwise restore based on filtered status
          else if (element.userData._wasFiltered) {
            // If it was filtered, reapply the filter effect
            if (element.userData.colorMaterial) {
              // Was colored and filtered
              if (!element.userData.fadedMaterial) {
                element.userData.fadedMaterial = element.userData.colorMaterial.clone();
              }
              element.material = element.userData.fadedMaterial;
            } else {
              // Was just filtered
              if (!element.userData.fadedMaterial) {
                element.userData.fadedMaterial = element.userData.originalMaterial.clone();
              }
              element.material = element.userData.fadedMaterial;
            }
            // Restore opacity
            element.material.transparent = true;
            element.material.opacity = element.userData._filteredOpacity;
            element.userData.filtered = true;
            
            // Clean up temporary state
            delete element.userData._wasFiltered;
            delete element.userData._filteredOpacity;
          } else if (element.userData.colorMaterial) {
            // Was just colored, not filtered
            element.material = element.userData.colorMaterial;
          } else if (element.userData._tempMaterial) {
            // Restore temporarily stored material
            element.material = element.userData._tempMaterial;
            delete element.userData._tempMaterial;
          } else if (element.userData.originalMaterial) {
            // Had no special state but has original material stored
            element.material = element.userData.originalMaterial;
          } else {
            // Fallback to model lookup (original behavior)
            scope.model.forEach((models) => {
              if (element.name == models.name) {
                if (element.userData.opacity != undefined) {
                  element.material.color.setHex(models.material.color.getHex());
                } else {
                  element.material = models.material;
                }
              }
            });
          }
          // Clear selection material reference
          delete element.userData.selectMaterial;
        }
      });
      
      scope.intersectedArray = [];
      if (render) {
        this.viewerMgrInstance.render();
      }
      
      return {
        "status": "success",
        "message": "Cleared the selected element."
      };
    }
    else {
      return {
        "status": "failed",
        "message": "ERROR : Select the element from loaded model."
      };
    }
  }
  catch (err) {
    return {
      "status": "failed",
      "message": 'System Error: ' + err.message
    };
  }
}


/**
 * Get Selected elements names
 * @returns {Object | Array<string>} List of the elements names
 * Or Return error message
 * { status:  mesage: }
 */
export function getSelected() {
  var scope = this;
  try {
    if (scope.intersectedArray.length >= 1) {
      var arrayElements = [];
      scope.intersectedArray.forEach((i) => {
        arrayElements.push(i.name);
      });
      //viewerMgrInstance.render();
      return arrayElements;
    }
    else {
      var returnValue = {
        "status": "failed",
        "message": "ERROR : Select the element from loaded model."
      }
      return returnValue;
    }
  }
  catch (err) {
    var returnValue = {
      "status": "failed",
      "message": 'System Error: ' + err.message
    }
    return returnValue;
  }
}

/**
 * Clears Selection
 * @returns {Object} Return success or error messages 
 * { status:  mesage: }
 */
export function clearSelection() {
  var scope = this;
  var response = scope.clearPickSelection();
  return response;
}


/**
 * Selects single element
 * @param {string} element_id - Element id
 * @returns {Object} - Return success or error messages 
 * { status:  mesage: }
 */
export function selectSingleElement(element_id) {
  var scope = this;
  try {
    scope.clearPickSelection();
    if (scope.modelScene.children.length != 0) {
      let child = scope.elementsMap.get(element_id);
      if (child instanceof THREE.Mesh) {
        scope.applySelectionMaterial(element_id);
      }
      var returnValue = {
        "status": "success",
        "message": "Selected element API"
      }
      return returnValue;
    }
    else {
      var returnValue = {
        "status": "failed",
        "message": "ERROR : Model is not loaded in viewer."
      }
      return returnValue;
    }
  }
  catch (err) {
    var returnValue = {
      "status": "failed",
      "message":  'System Error: ' + err.message
    }
    return returnValue;
  }
}


/**
 * Selects Multiple element
 * @param {Array<string>} element_Ids - Element Ids
 * @param {boolean} clear - Clears selection before selection if true
 * @returns {Object} - Return success or error messages 
 * { status:  mesage: }
 */
export function selectMultipleElements(element_Ids, clear) {
  var scope = this;
  try {
    if (scope.modelScene.children.length != 0 && element_Ids.length >= 1) {
      if (clear) {
        scope.clearPickSelection();
      }

      for (let i = 0; i < element_Ids.length; i++) {
        scope.selectSingleElement(element_Ids[i]);
      }

      var returnValue = {
        "status": "success",
        "message": "Select element API"
      }
      return returnValue;
    }
    else {
      var returnValue = {
        "status": "failed",
        "message": "ERROR : Model is not loaded in viewer."
      }
      return returnValue;
    }
  }
  catch (err) {
    var returnValue = {
      "status": "failed",
      "message": 'System Error: ' + err.message
    }
    return returnValue;
  }
}
